import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Form } from '@/components/ui/form';
import { type ExamFormData, defaultExamValues } from './utils/examValidation';
import { ExamTabs } from './ExamTabs';
import { PatientDemographicsCard } from './PatientDemographicsCard';
import type { PersonInput } from '@/lib/assessment/namatAssessment';
import { saveClinicalExam, updateExam, canTakeExam } from '@/lib/firebase/queries/exams';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Customer, ClinicalExam } from './types';
import { UseFormReturn } from 'react-hook-form';

interface ExamDialogProps {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  form: UseFormReturn<ExamFormData>;
  isEditing?: boolean;
  examDate?: string;
  onSave: () => Promise<void>;
  onComplete: () => Promise<void>;
  isSaving: boolean;
}

export function ExamDialog({
  customer,
  open,
  onOpenChange,
  onSuccess,
  form,
  isEditing,
  onSave,
  isSaving
}: ExamDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [person, setPerson] = useState<PersonInput>({});
  const [isChecking, setIsChecking] = useState(true);
  const [existingExam, setExistingExam] = useState<ClinicalExam | null>(null);
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  
  useEffect(() => {
    const checkExamEligibility = async () => {
      if (!customer) return;

      try {
        setIsChecking(true);
        const { existingExam } = await canTakeExam(customer.id);
        
        if (existingExam) {
          console.log('📋 Loading existing exam data:', existingExam);
          console.log('🔍 Existing exam nutrition data:', existingExam.nutrition);
          console.log('🔍 Existing exam exercise data:', existingExam.exercises);
          console.log('🔍 Existing exam sleep data:', existingExam.sleep);
          setExistingExam(existingExam);
          form.reset(existingExam);
          console.log('📝 Form values after reset:', form.getValues());
        } else {
          console.log('🆕 No existing exam found, using default values');
          setExistingExam(null);
          form.reset(defaultExamValues);
        }
      } catch (error) {
        console.error('Error checking exam eligibility:', error);
        toast.error('Failed to check exam eligibility');
      } finally {
        setIsChecking(false);
      }
    };

    if (open && customer) {
      checkExamEligibility();
    }
  }, [customer, open, form]);

  // Function to clean undefined values from data
  // Firebase rejects undefined: replace it with null, recursively, keeping the shape.
  const cleanData = <T,>(obj: T): T => {
    if (obj === null || obj === undefined) return null as T;
    if (Array.isArray(obj)) return obj.map(cleanData) as T;
    if (typeof obj === 'object') {
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        cleaned[key] = value === undefined ? null : cleanData(value);
      }
      return cleaned as T;
    }
    return obj;
  };

  const onSubmit = async (data: ExamFormData) => {
    console.log('🚀 onSubmit called with data:', data);
    console.log('🔍 Customer:', customer);
    console.log('🔍 Current user:', currentUser);
    
    if (!customer || !currentUser) {
      console.error('❌ Missing customer or currentUser');
      return;
    }

    try {
      console.log('⏳ Starting save process...');
      setIsLoading(true);
      
      // Clean the data to remove undefined values
      console.log('📊 Original data before cleaning:', data);
      const cleanedData = cleanData(data);
      console.log('🧹 Cleaned data after processing:', cleanedData);
      
      // Log specific sections to check if answers are preserved
      console.log('🍎 Nutrition answers:', cleanedData.nutrition);
      console.log('🏃 Exercise answers:', cleanedData.exercises);
      console.log('😴 Sleep answers:', cleanedData.sleep);
      console.log('😊 Mood answers:', cleanedData.mood);
      console.log('👥 Relations answers:', cleanedData.relations);
      console.log('📱 Addictions answers:', cleanedData.addictions);
      
      if (existingExam) {
        console.log('📝 Updating existing exam:', existingExam.id);
        const updateData = {
          ...cleanedData,
          status: 'completed',
          updatedAt: new Date()
        };
        console.log('📊 Update data being sent:', updateData);
        await updateExam(existingExam.id, updateData);
        console.log('✅ Exam updated successfully');
        toast.success('Examination updated successfully');
      } else {
        console.log('🆕 Creating new exam');
        const examData = {
          ...cleanedData,
          customerId: customer.id,
          examinerId: currentUser.uid,
          date: new Date(),
          status: 'completed'
        };
        console.log('📋 Exam data to save:', examData);
        const savedExamId = await saveClinicalExam(examData);
        console.log('✅ Exam saved successfully with ID:', savedExamId);
        toast.success('Examination completed successfully');
      }

      console.log('🔄 Resetting form and closing dialog');
      form.reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('❌ Error saving exam:', error);
      toast.error('Failed to save examination');
    } finally {
      setIsLoading(false);
      console.log('🏁 Save process completed');
    }
  };

  if (isChecking) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.examiner.examination.title.loading')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[65%] w-[65%]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('dashboard.examiner.examination.title.edit')
              : t('dashboard.examiner.examination.title.record')}
          </DialogTitle>
        </DialogHeader>

        {customer && <PatientDemographicsCard userId={customer.id} onChange={(d) => setPerson({ birthDate: d.dateOfBirth, sex: d.gender })} />}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.log('❌ Form validation errors:', errors);
            console.log('📝 Current form values:', form.getValues());
            
            // Log specific error details
            Object.keys(errors).forEach(key => {
              console.log(`🔴 Error in ${key}:`, errors[key as keyof typeof errors]);
            });
          })} className="space-y-4">
            <ExamTabs form={form} person={person} />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  console.log('🚪 Cancel button clicked');
                  onOpenChange(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('dashboard.examiner.examination.actions.cancel')}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    console.log('💾 Save button clicked');
                    onSave();
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving
                    ? t('dashboard.examiner.examination.actions.saving')
                    : t('dashboard.examiner.examination.actions.update')}
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading}
                onClick={() => {
                  console.log('✅ Complete button clicked');
                  console.log('🔍 Form valid:', form.formState.isValid);
                  console.log('🔍 Form errors:', form.formState.errors);
                  console.log('🔍 Is loading:', isLoading);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-[#7dda55] rounded-md hover:bg-[#6dc447] disabled:opacity-50 flex items-center gap-2"
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {isLoading
                  ? t('dashboard.examiner.examination.actions.saving')
                  : t('dashboard.examiner.examination.actions.complete')}
              </button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}